import {
  ClipboardCheck,
  ScanLine,
  Target,
  CalendarDays,
  Activity,
} from "lucide-react";

export const SaintWeek = () => (
  <section className="py-16 sm:py-20 bg-muted/30">
    <div className="container-px mx-auto max-w-6xl px-4">
      <div className="text-center mb-10 sm:mb-12">
        <h2 className="font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
          What the week looks like
        </h2>
        <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
          Five consecutive days of intensive, precision-targeted sessions with
          plenty of restful downtime in our private lounge suites.
        </p>
      </div>

      {/* 5-Day Visual Timeline Infographic */}
      <div className="mb-12 mx-auto max-w-4xl overflow-hidden rounded-3xl border border-border bg-card p-3 sm:p-5 shadow-premium">
        <img
          src="https://vibe.filesafe.space/1787327131284421342/attachments/ab919b7e-333a-4b45-a6ed-e22dbaaee640.png"
          alt="What a SAINT® TMS Week Can Look Like: Day 1 Evaluation and first treatment day, Day 2 Multiple brief sessions with breaks, Day 3 Continued accelerated schedule, Day 4 Ongoing sessions in supportive clinic setting, Day 5 Final day of 5-day protocol"
          className="w-full h-auto rounded-2xl object-contain"
          loading="lazy"
          width={1200}
          height={900}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-5">
        {[
          {
            icon: ClipboardCheck,
            step: "1",
            title: "Consultation and screening (Zoom web conference)",
            body: "Your journey starts with a convenient, no-obligation Zoom call with our clinical care team to review your diagnosis, medication history, and fMRI safety. As your evaluation evolves, you have the option to meet with our psychiatrist via Zoom web conference or in person at our clinic.",
          },
          {
            icon: ScanLine,
            step: "2",
            title: "fMRI brain scan (mandatory in person)",
            body: "A mandatory in-person resting-state fMRI (roughly 45 minutes) maps your brain's individual circuits. We coordinate the entire imaging order for you at our partner facility.",
          },
          {
            icon: Target,
            step: "3",
            title: "Personalized targeting",
            body: "Magnus Medical's SNT™ software maps your brain's connectivity and identifies your individual stimulation target within the dorsolateral prefrontal cortex.",
          },
          {
            icon: CalendarDays,
            step: "4",
            title: "Five treatment days (mandatory in person)",
            body: "Mandatory in-person treatment Monday through Friday in our Coral Springs clinic. Ten 10-minute sessions per day with 50-minute breaks in our private lounge suites. You remain awake and alert throughout.",
          },
          {
            icon: Activity,
            step: "5",
            title: "Follow-up (Zoom or in person)",
            body: "Your care team and psychiatrist monitor your progress after remission, with flexible follow-up consultations available either in person or via Zoom web conference.",
          },
        ].map((s) => (
          <div
            key={s.step}
            className="rounded-2xl border border-border bg-card p-5 shadow-soft flex flex-col"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-accent text-sm font-bold text-accent-foreground">
                {s.step}
              </span>
              <s.icon className="h-5 w-5 text-accent shrink-0" />
            </div>
            <h3 className="font-headings text-base font-bold text-primary leading-snug">
              {s.title}
            </h3>
            <p className="mt-2 text-sm text-foreground/80 leading-relaxed flex-1">
              {s.body}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {/* Step Visual 1: Personalized Targeting (fMRI Brain Map) */}
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft flex flex-col">
          <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
            <img
              src="https://vibe.filesafe.space/1787327131284421342/attachments/61fd8a15-3767-464e-99b6-5a1351dea1b3.png"
              alt="Personalized fMRI brain scan mapping the individual stimulation target for SAINT® precision TMS treatment"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-accent">
                Step 2 &amp; 3 · Imaging &amp; Targeting
              </span>
              <h3 className="mt-2 font-headings text-lg sm:text-xl font-bold text-primary">
                Personalized fMRI Brain Targeting
              </h3>
              <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                A resting-state functional MRI maps your brain's individual
                connectivity to pinpoint the exact stimulation target within the
                dorsolateral prefrontal cortex before treatment starts.
              </p>
            </div>
          </div>
        </div>

        {/* Step Visual 2: Stimulation Series (Patient in Treatment Chair) */}
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft flex flex-col">
          <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
            <img
              src="https://vibe.filesafe.space/1787327131284421342/attachments/9907b0c5-c62d-4096-ba03-9d7a9cc3ffc5.png"
              alt="Patient comfortably seated in treatment chair undergoing SAINT® 5-day accelerated TMS stimulation series"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-accent">
                Step 4 · Five Treatment Days
              </span>
              <h3 className="mt-2 font-headings text-lg sm:text-xl font-bold text-primary">
                Ten 10-Minute Sessions Daily
              </h3>
              <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                Precision stimulation with no anesthesia or downtime. Ten
                10-minute sessions per day while you remain awake and alert,
                supported by our dedicated on-site clinical team.
              </p>
            </div>
          </div>
        </div>

        {/* Step Visual 3: Dedicated Private Lounge Suite */}
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft flex flex-col">
          <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
            <img
              src="https://vibe.filesafe.space/1787327131284421342/attachments/65bd3a1f-5ba1-43da-8b0f-00417c5d48ed.png"
              alt="Private furnished suite in the SAINT® Lounge Wing with leather recliner, sectional sofa, desk, coffee and beverage station"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-accent">
                Between Sessions · 50-Min Breaks
              </span>
              <h3 className="mt-2 font-headings text-lg sm:text-xl font-bold text-primary">
                Dedicated Private Lounge Wing
              </h3>
              <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                Relax, stream 4K TV, work at a quiet desk, have your lunch, or
                spend time with a visiting loved one. Our care team ensures you
                feel right at home all day.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);
